/**
 * Hook that triggers AI extraction across all uploaded documents for
 * a player and document type.
 *
 * Calls the `triggerContractExtraction` GraphQL mutation, writes the
 * optimistic `PROCESSING` status into the `PlayerContractsOverview`
 * Apollo cache (so the Squad > Contracts table flips to "PROCESSING"
 * immediately — no network round-trip required), AND refetches the
 * drawer's active `GetContractExtraction` observer (so the open
 * drawer's `useContractExtraction` consumer no longer reports stale
 * NULL after a successful trigger).
 *
 * SE-4817: prior to this hook, the mutation only refetched the
 * drawer's `GetContractExtraction` observer which left the table
 * stale until the user manually refreshed. The cache write below
 * is what surfaces the running extraction in the Transfer / Contract
 * status column without a refetch.
 *
 * SE-4823: dropping the refetch entirely (as SE-4817 first did) left
 * the drawer's observer stale after a successful trigger, so the
 * "Extract Key Terms" button stayed visible and `isExtracting` stayed
 * false. Re-clicking hit the backend's conflict guard
 * (`contracts.service.ts:838-851`) and surfaced "An active extraction
 * already exists for this player and document type." even though the
 * first call had succeeded. The fix keeps the SE-4817 cache write
 * AND restores the refetch of the active drawer observer in the same
 * mutation call, routed through `activeContractExtractionRefetch` so
 * the variable shape can never drift from the observer's.
 *
 * The mutation document is defined inline via `gql` rather than
 * imported from `generated/graphql.ts` so the hook can ship before
 * the codegen step runs against the updated schema.
 * @module features/contracts/hooks/useTriggerExtraction
 */

import { type ApolloCache, gql, useMutation } from "@apollo/client";
import { useCallback, useMemo, useRef, useState } from "react";

import { activeContractExtractionRefetch } from "./contractExtractionRefetch";
import {
  ContractExtractionStatus,
  DocumentType,
  PlayerContractsOverviewDocument,
  type PlayerContractsOverviewQuery,
} from "@/generated/graphql";

/** GraphQL document for the TriggerContractExtraction mutation. */
export const TRIGGER_CONTRACT_EXTRACTION_MUTATION = gql`
  mutation TriggerContractExtraction($input: TriggerContractExtractionInput!) {
    triggerContractExtraction(input: $input) {
      id
      status
    }
  }
`;

/** Shape of the TriggerContractExtraction mutation response. */
interface TriggerContractExtractionResponse {
  readonly triggerContractExtraction: {
    readonly id: string;
    readonly status: string;
  };
}

/** Shape of the TriggerContractExtraction mutation variables. */
interface TriggerContractExtractionVariables {
  readonly input: {
    readonly playerId: string;
    readonly documentType: string;
  };
}

/** Public API returned by `useTriggerExtraction`. */
export interface UseTriggerExtractionResult {
  /** Triggers extraction and writes PROCESSING into the overview cache on success. */
  readonly triggerExtraction: (
    playerId: string,
    documentType: string
  ) => Promise<boolean>;
  /** Whether the trigger mutation is currently in flight. */
  readonly isTriggering: boolean;
  /** Error message from the last failed trigger attempt, or null. */
  readonly errorMessage: string | null;
}

/**
 * Picks the player-overview field that mirrors the triggered document
 * type. `TRANSFER_AGREEMENT` updates `transferStatus`; everything else
 * (currently only `CONTRACT`) updates `contractStatus`.
 * @param documentType - Document type from the mutation input
 * @returns The row field name to flip to `PROCESSING`
 */
function fieldForDocumentType(
  documentType: string
): "transferStatus" | "contractStatus" {
  return documentType === DocumentType.TransferAgreement
    ? "transferStatus"
    : "contractStatus";
}

/**
 * Writes `PROCESSING` into the matching player row of a single
 * `PlayerContractsOverview(archivedOnly: …)` cache entry. Defensive:
 * does nothing when the cache entry is missing or the player row is
 * absent — never refetches and never crashes.
 * @param cache - Apollo cache instance from the mutation `update` hook
 * @param playerId - Target player UUID from the mutation input
 * @param fieldName - `transferStatus` or `contractStatus`
 * @param archivedOnly - Cache variant key (`false` for Active, `true` for Archived)
 */
function writeProcessingToOverviewVariant(
  cache: ApolloCache<unknown>,
  playerId: string,
  fieldName: "transferStatus" | "contractStatus",
  archivedOnly: boolean
): void {
  const existing = cache.readQuery<PlayerContractsOverviewQuery>({
    query: PlayerContractsOverviewDocument,
    variables: { archivedOnly },
  });
  if (!existing) return;

  const players = existing.playerContractsOverview.players;
  const matchingIndex = players.findIndex(p => p.playerId === playerId);
  if (matchingIndex === -1) return;

  const updatedPlayers = players.map((player, index) =>
    index === matchingIndex
      ? { ...player, [fieldName]: ContractExtractionStatus.Processing }
      : player
  );

  cache.writeQuery<PlayerContractsOverviewQuery>({
    query: PlayerContractsOverviewDocument,
    variables: { archivedOnly },
    data: {
      playerContractsOverview: {
        ...existing.playerContractsOverview,
        players: updatedPlayers,
      },
    },
  });
}

/**
 * Wraps the TriggerContractExtraction mutation with loading state,
 * error tracking, an Apollo cache write that surfaces the new
 * `PROCESSING` status in the Squad > Contracts table without a
 * network refetch (SE-4817), and a refetch of the drawer's active
 * `GetContractExtraction` observer so `useContractExtraction`
 * consumers no longer report stale NULL after a successful trigger
 * (SE-4823).
 * @returns Imperative trigger function + loading/error state
 */
export function useTriggerExtraction(): UseTriggerExtractionResult {
  const [runMutation] = useMutation<
    TriggerContractExtractionResponse,
    TriggerContractExtractionVariables
  >(TRIGGER_CONTRACT_EXTRACTION_MUTATION);

  const [isTriggering, setIsTriggering] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inFlightRef = useRef(false);

  const triggerExtraction = useCallback(
    async (playerId: string, documentType: string): Promise<boolean> => {
      if (inFlightRef.current) return false;
      inFlightRef.current = true;
      setIsTriggering(true);
      setErrorMessage(null);
      try {
        const fieldName = fieldForDocumentType(documentType);
        const { data } = await runMutation({
          variables: {
            input: { playerId, documentType },
          },
          update: cache => {
            // Update both archivedOnly variants defensively. Either
            // (or both) may be missing from the cache depending on
            // which list the user has loaded so far.
            writeProcessingToOverviewVariant(cache, playerId, fieldName, false);
            writeProcessingToOverviewVariant(cache, playerId, fieldName, true);
          },
          // SE-4823: refetch the drawer's active GetContractExtraction
          // observer so `useContractExtraction` flips from NULL to the
          // newly-created PROCESSING row, hiding the "Extract Key Terms"
          // button and preventing the user from triggering a second
          // mutation that would hit the backend's conflict guard.
          refetchQueries: [
            activeContractExtractionRefetch(playerId, documentType),
          ],
          awaitRefetchQueries: true,
        });
        return data?.triggerContractExtraction?.id != null;
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Extraction failed.";
        setErrorMessage(message);
        return false;
      } finally {
        setIsTriggering(false);
        inFlightRef.current = false;
      }
    },
    [runMutation]
  );

  return useMemo(
    () => ({ triggerExtraction, isTriggering, errorMessage }),
    [triggerExtraction, isTriggering, errorMessage]
  );
}
